import { useMutation } from '@apollo/client/react';

import {
  ChatType,
  GenerateAssetUploadUrlRequest,
  InstagramTargetType,
  LazadaTargetType,
  LineMessageType,
  LineTargetType,
  MessengerTargetType,
  ShopeeTargetType,
  WhatsAppTargetType,
} from '@anyx/common-graphql';
import { reportAppError } from '@anyx/shared-utils';

import { GENERATE_ASSET_UPLOAD_URL_V2 } from '../graphql';

interface GenerateAssetUrlParams {
  file: File;
  messageType?: LineMessageType;
}

/**
 * Resolve the MIME type for a file based on its extension.
 * This ensures that PDF, XLS, and XLSX files use their standard MIME types
 * regardless of what the browser detects.
 */
const resolveMimeType = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'application/pdf';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls':
      return 'application/vnd.ms-excel';
    default:
      return file.type;
  }
};

type UseAssetUploadType =
  | LineTargetType
  | InstagramTargetType
  | MessengerTargetType
  | WhatsAppTargetType
  | ShopeeTargetType;

type MutationContentType = Omit<GenerateAssetUploadUrlRequest, 'fileName' | 'mimeType'>;

const setAssetMutationContent = (
  platform: ChatType,
  type: UseAssetUploadType,
  messageType?: LineMessageType
): MutationContentType | void => {
  if (platform === 'LINE') {
    // LINE has no CHAT_VIDEO_MESSAGE target — map it to CHAT_MESSAGE with VIDEO messageType
    const lineType = type === 'CHAT_VIDEO_MESSAGE' ? LineTargetType.CHAT_MESSAGE : type as LineTargetType;
    const lineMessageType = type === 'CHAT_VIDEO_MESSAGE' ? LineMessageType.VIDEO : messageType;
    return {
      lineTarget: {
        type: lineType,
        messageType: lineMessageType,
      },
    };
  }

  if (type in InstagramTargetType && platform === 'INSTAGRAM') {
    return {
      instagramTarget: {
        type: type as InstagramTargetType,
      },
    };
  }

  if (type in MessengerTargetType && platform === 'MESSENGER') {
    return {
      messengerTarget: {
        type: type as MessengerTargetType,
      },
    };
  }

  if (type in WhatsAppTargetType && platform === 'WHATSAPP') {
    return {
      whatsAppTarget: {
        type: type as WhatsAppTargetType,
      },
    };
  }

  if (type in LazadaTargetType && platform === 'LAZADA') {
    return {
      lazadaTarget: {
        type: type as LazadaTargetType,
      },
    };
  }

  if (type in ShopeeTargetType && platform === 'SHOPEE') {
    return {
      shopeeTarget: {
        type: type as ShopeeTargetType,
      },
    };
  }
};

export const useAssetUpload = (platform: ChatType, type: UseAssetUploadType) => {
  const [assetMutation] = useMutation(GENERATE_ASSET_UPLOAD_URL_V2);

  const generateAssetUrl = async ({ file, messageType }: GenerateAssetUrlParams) => {
    const mimeType = resolveMimeType(file);

    const { data } = await assetMutation({
      variables: {
        input: {
          fileName: file.name,
          mimeType,
          ...setAssetMutationContent(platform, type, messageType),
        },
      },
    });
    const uploadUrl = data?.GenerateAssetUploadUrlV2?.originalUrl.url;

    if (!uploadUrl) {
      throw new Error('no upload url');
    }

    return uploadUrl;
  };

  const uploadAsset = async (generateAssetUrlFn: () => Promise<string>, file: File) => {
    try {
      const url = await generateAssetUrlFn();
      const mimeType = resolveMimeType(file);

      await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': mimeType,
        },
      });

      const urlObject = new URL(url);

      return `${urlObject.origin}${urlObject.pathname}`;
    } catch (error) {
      reportAppError(error);
      return undefined;
    }
  };

  return {
    generateAssetUrl,
    uploadAsset,
  };
};
